import React, { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { isNull, isUndefined } from 'lodash';
import { Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { searchForPropertyApplicant, setApplicantFilledFields } from '../../../../redux/modules/searchApplicant';
import FormSection from '../../../../components/FormComponents/FormSection';
import ChangeVictim from '../../../../components/ClaimFormComponents/ChangeVictim';
import PropertyPhysicalSearchApplicantForm from './PropertyPhysicalSearchApplicantForm';
import PropertyLegalSearchApplicantForm from './PropertyLegalSearchApplicantForm';
import usePrevious from '../../../../hooks/usePrevious';
import { updatePropertyClaimForm } from '../../../../helpers/claims/propertyClaim';
import { updatePropertyForm } from '../../../../redux/modules/propertyClaim';
import { prepareSearchRequestsPayload } from '../../../../helpers/claims';
import CustomErrorMessage from '../../../../components/ClaimFormComponents/CustomErrorMessage';
import { APPLICANT_TYPES } from '../../../../constants';

import UserInfoIcon from '../../../../img/formSectionIcons/userInfo.svg';
import styles from '../../../CompensationContainer/Steps/steps.module.scss';

const PropertySearchApplicantStep = ({
  params: { setStepNavigationBox, loader, scrollTo },
  blockNextSteps,
  step,
  goNext,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [status, setStatus] = useState();

  const countries = useSelector((store) => store.countriesReducer.countries);
  const { isSearching, searchData } = useSelector(
    (store) => store.searchApplicant,
    shallowEqual,
  );
  const { applicant } = searchData;
  const { personalData } = useSelector((store) => store.propertyClaim, shallowEqual);

  useEffect(() => {
    if (isUndefined(status)) {
      if (!isNull(personalData)) {
        setStatus(personalData.applicantType);
      } else {
        setStatus(APPLICANT_TYPES[0].value);
      }
    }
  }, [status, personalData]);

  const handleRadioChange = (val) => {
    setStatus(val);
    blockNextSteps(step);
  };

  const handleSearch = useCallback(
    (data) => {
      const payload = prepareSearchRequestsPayload(data);
      blockNextSteps(step);
      dispatch(searchForPropertyApplicant({ ...payload, status }));
      dispatch(setApplicantFilledFields({ ...payload, status }));
    },
    [blockNextSteps, dispatch, status, step],
  );

  const previousIsSearching = usePrevious(isSearching);

  useEffect(() => {
    if (
      !isNull(applicant)
        && previousIsSearching === true
        && !isSearching
    ) {
      goNext();
    }
  }, [applicant, goNext, isSearching, previousIsSearching]);

  useEffect(() => {
    updatePropertyClaimForm(status, countries, dispatch, updatePropertyForm, applicant);
  }, [status, countries, dispatch, applicant]);

  return (
    <div ref={scrollTo} className={styles.formContainer}>
      {isSearching && <div>{loader}</div>}
      <FormSection title="headerTitles.insuredData" icon={UserInfoIcon}>
        <ChangeVictim onChange={handleRadioChange} options={APPLICANT_TYPES} status={status} />
        {status === APPLICANT_TYPES[0].value && (
        <PropertyPhysicalSearchApplicantForm
          handleSearch={handleSearch}
          setStepNavigationBox={setStepNavigationBox}
          isSearching={isSearching}
        />
        )}
        {status === APPLICANT_TYPES[1].value && (
        <PropertyLegalSearchApplicantForm
          handleSearch={handleSearch}
          setStepNavigationBox={setStepNavigationBox}
          isSearching={isSearching}
        />
        )}
      </FormSection>
      {searchData.customError && (
      <Grid item xs={12}>
        <CustomErrorMessage customErrorCode={t(`backendErrors.${searchData.customError.code}`)} />
      </Grid>
      )}
    </div>
  );
};

PropertySearchApplicantStep.propTypes = {
  params: PropTypes.shape({
    setStepNavigationBox: PropTypes.func.isRequired,
    scrollTo: PropTypes.func.isRequired,
    loader: PropTypes.element.isRequired,
  }).isRequired,
  blockNextSteps: PropTypes.func.isRequired,
  step: PropTypes.shape({
    n: PropTypes.number.isRequired,
  }).isRequired,
  goNext: PropTypes.func.isRequired,
};

export default PropertySearchApplicantStep;
